'use client'

import { type Address, MathLib } from '@morpho-org/blue-sdk'
import { safeParseNumber } from '@morpho-org/blue-sdk-viem'
import {
  type ApiInputToken,
  InterfaceChainUtils,
  parseApiAsset,
  type Rewards,
  type VaultRewards
} from '@morpho-org/dapp-core'
import { bigIntComparator } from '@morpho-org/morpho-ts'

import type { AggregatedApy, NetApy, RewardToken } from '@/components/common/Card'
import type { UserEarnPosition } from '@/hooks/positions/userEarnPositionsQuery'

import { vaultIncludesDonations } from '@/config/donation-vaults'
import { getParsedValue } from '@/helpers/formatters'
import { normalizeRewards } from '@/helpers/rewards'
import { getVaultUrl } from '@/helpers/routing'
import { getShareFromTotal } from '@/helpers/vault'
import { isVaultV2RateCapped, toVaultV2ApyGross } from '@/helpers/vaultV2RateCap'
import { getVaultV1Apy } from '@/hooks/useVaultV1Apy'
import { getVaultV2Apy } from '@/hooks/useVaultV2Apy'

/** A BFF asset payload — `chainId` is flat (vs `ApiInputToken`'s nested `chain.id`). */
interface BffFlatAsset {
  chainId: number
  address: string
  symbol: string
  name?: string | null
  decimals: number
  logoURI: string | null
  priceUsd?: number | null
  yield?: number | null
}

/**
 * BFF vault/market asset (flat `chainId`) → `ApiInputToken` (`chain.id` nested).
 * `name` falls back to `symbol` (apy / allocation payloads omit `name`).
 */
const toApiInputToken = (asset: BffFlatAsset): ApiInputToken => ({
  // oxlint-disable-next-line typescript/consistent-type-assertions
  address: asset.address as Address,
  symbol: asset.symbol,
  name: asset.name ?? asset.symbol,
  decimals: asset.decimals,
  logoURI: asset.logoURI,
  chain: { id: asset.chainId },
  priceUsd: asset.priceUsd ?? null,
  yield: asset.yield
})

/** A `user.earnPositions` token (`logoUri`, nullable `symbol`) → `ApiInputToken`. */
const fromEarnPositionToken = (asset: UserEarnPosition['vault']['asset']): ApiInputToken =>
  toApiInputToken({
    ...asset,
    symbol: asset.symbol ?? '',
    logoURI: asset.logoUri,
    yield: asset.yieldApy
  })

/**
 * Accumulate one position's reward APRs into the shared reward-token map: sum
 * the USD-weighted total APR and push a per-position breakdown item. Shared by
 * the V1 and V2 earn branches, which differ only by the `isVaultV2` flag.
 */
const accumulateRewardTokens = (
  rewardTokens: Record<string, RewardToken>,
  rewards: Rewards[],
  item: {
    name: string
    weight: bigint
    chainId: number
    logoURIs: NetApy['logoURIs']
    isVaultV2: boolean
  }
) => {
  rewards.forEach(reward => {
    const entry = (rewardTokens[reward.rewardToken.address] ??= {
      totalApr: 0n,
      asset: {
        symbol: reward.rewardToken.symbol ?? '',
        logoURI: reward.rewardToken.logoURI ?? ''
      },
      items: []
    })

    entry.totalApr += MathLib.wMulDown(reward.apr ?? 0n, item.weight)
    entry.items.push({
      name: item.name,
      apr: reward.apr ?? 0n,
      weight: item.weight,
      chainId: item.chainId,
      logoURIs: item.logoURIs,
      allocation: 0n,
      percentageOffset: 0n,
      isVaultV2: item.isVaultV2
    })
  })
}

/**
 * Earn aggregation over `user.earnPositions`.
 * - the per-position weight is `position.assetsUsd` (already USD-denominated).
 * - V1 rewards come straight from `vault.rewards`; the BFF pre-sums forwarded
 *   market rewards, so allocations don't need walking here.
 */
export const calculateEarnAggregatedData = (
  earnPositions: UserEarnPosition[]
): AggregatedApy | null => {
  try {
    if (earnPositions.length === 0) return null

    let totalAssetsUsd = 0n
    let weightedEarnApy = 0n
    let weightedVaultApy = 0n
    const rewardTokens: Record<string, RewardToken> = {}
    const vaultApys: NetApy[] = []

    earnPositions
      .filter(position => position.assetsUsd != null && position.assetsUsd > 0)
      .forEach(position => {
        const assetsUsd = getParsedValue(position.assetsUsd, safeParseNumber) ?? 0n
        if (assetsUsd <= 0n) return

        const vault = position.vault
        const asset = fromEarnPositionToken(vault.asset)
        const chainId = vault.chainId
        const isVaultV2 = vault.version.startsWith('2')
        const vaultRewards = vault.rewards.map(
          (reward): VaultRewards => ({
            asset: fromEarnPositionToken({ ...reward.asset, priceUsd: null, yieldApy: null }),
            supplyApr: reward.supplyApr
          })
        )

        totalAssetsUsd += assetsUsd

        if (isVaultV2) {
          const {
            apy,
            netApy,
            maxRate,
            rewards,
            performanceFee: v2PerformanceFee,
            managementFee: v2ManagementFee,
            points: v2Points
          } = getVaultV2Apy({
            vaultV2: {
              address: vault.address,
              asset,
              performanceFee: vault.performanceFee,
              managementFee: vault.managementFee,
              netApyExcludingRewards: vault.apy.native,
              netApy: vault.apy.net,
              maxRate: vault.maxApy,
              apyBreakdown: vault.apyBreakdown,
              rewards: vaultRewards
            }
          })

          if (netApy) {
            weightedEarnApy += MathLib.wMulDown(netApy, assetsUsd)
          }

          weightedVaultApy += MathLib.wMulDown(apy ?? 0n, assetsUsd)

          accumulateRewardTokens(rewardTokens, rewards, {
            name: vault.name,
            weight: assetsUsd,
            chainId,
            logoURIs: [asset.logoURI],
            isVaultV2: true
          })

          const identifier = InterfaceChainUtils.getIdentifierById(chainId)

          vaultApys.push({
            name: vault.name,
            apy: netApy ?? 0n,
            weight: assetsUsd,
            allocation: 0n,
            percentageOffset: 0n,
            chainId,
            vaultAddress: vault.address,
            logoURIs: [asset.logoURI],
            isVaultV2: true,
            maxRate,
            href: getVaultUrl({
              identifier,
              vaultAddress: vault.address,
              vaultName: vault.name
            }),
            hoverCardProps: {
              nativeApy: apy,
              netApy,
              performanceFee: v2PerformanceFee,
              managementFee: v2ManagementFee,
              rewards,
              asset: parseApiAsset(asset),
              points: v2Points,
              maxApy: vault.maxApy,
              isRateCapped: isVaultV2RateCapped(
                toVaultV2ApyGross({
                  apyBreakdown: vault.apyBreakdown,
                  netApyExcludingRewards: vault.apy.native,
                  fees: { performanceFee: vault.performanceFee, managementFee: vault.managementFee }
                }),
                vault.maxApy
              ),
              includesDonations: vaultIncludesDonations(chainId, vault.address),
              apyBreakdown: vault.apyBreakdown
            }
          })

          return
        }

        const { apy, netApy, performanceFee, rewards } = getVaultV1Apy({
          vaultV1: {
            address: vault.address,
            asset,
            state: {
              fee: vault.performanceFee,
              netApy: vault.apy.net,
              netApyExcludingRewards: vault.apy.native,
              allocation: null
            },
            // `vault.rewards` already includes forwarded market rewards (VAU-1774).
            rewards: normalizeRewards(vaultRewards)
          }
        })

        if (netApy) {
          weightedEarnApy += MathLib.wMulDown(netApy, assetsUsd)
        }

        weightedVaultApy += MathLib.wMulDown(apy ?? 0n, assetsUsd)

        accumulateRewardTokens(rewardTokens, rewards, {
          name: vault.name,
          weight: assetsUsd,
          chainId,
          logoURIs: [asset.logoURI],
          isVaultV2: false
        })

        const identifier = InterfaceChainUtils.getIdentifierById(chainId)

        vaultApys.push({
          name: vault.name,
          apy: netApy ?? 0n,
          weight: assetsUsd,
          allocation: 0n,
          percentageOffset: 0n,
          chainId,
          logoURIs: [asset.logoURI],
          isVaultV2: false,
          href: getVaultUrl({
            identifier,
            vaultAddress: vault.address,
            vaultName: vault.name
          }),
          hoverCardProps: {
            nativeApy: apy,
            netApy,
            performanceFee,
            rewards
          }
        })
      })

    const avgEarnApy = totalAssetsUsd > 0n ? MathLib.wDivDown(weightedEarnApy, totalAssetsUsd) : 0n
    const avgVaultApy =
      totalAssetsUsd > 0n ? MathLib.wDivDown(weightedVaultApy, totalAssetsUsd) : 0n

    const normalizedRewardTokens: RewardToken[] = Object.values(rewardTokens).map(rewardToken => {
      const totalApr =
        totalAssetsUsd > 0n ? MathLib.wDivDown(rewardToken.totalApr, totalAssetsUsd) : 0n

      const itemTotalWeightedApr = rewardToken.items.reduce(
        (sum, i) => sum + MathLib.wMulDown(i.weight, i.apr),
        0n
      )

      let rewardSum = 0n

      return {
        ...rewardToken,
        totalApr,
        items: rewardToken.items
          .toSorted(bigIntComparator(({ weight }) => weight, 'desc'))
          .map(item => {
            const newItem = {
              ...item,
              allocation: getShareFromTotal(
                itemTotalWeightedApr,
                MathLib.wMulDown(item.weight, item.apr)
              ),
              percentageOffset: rewardSum
            }

            rewardSum += newItem.allocation

            return newItem
          })
      }
    })

    let vaultSum = 0n
    const normalizedVaultApys = vaultApys
      .toSorted(bigIntComparator(({ weight }) => weight, 'desc'))
      .map(item => {
        const newItem = {
          ...item,
          allocation: getShareFromTotal(totalAssetsUsd, item.weight),
          percentageOffset: vaultSum
        }

        vaultSum += newItem.allocation

        return newItem
      })

    return {
      apy: avgEarnApy,
      itemApy: avgVaultApy,
      rewardTokens: normalizedRewardTokens,
      itemApys: normalizedVaultApys
    }
  } catch (error) {
    throw new Error('Error calculating aggregated earn data for DashboardHeroApyCard', {
      cause: error
    })
  }
}
